import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addService, deleteService, updateService } from '../../firebase/firebase-delegate';
import { createBookingWebsiteDbRef } from '../../firebase/firebase-references';
import { sitePublishingKeys } from '../../sitePublishing/queryKeys';
import { Service } from '../../types';

export type ServiceDraft = Omit<Service, 'id'>;

// Matches where addService and the edit flow store a service's image.
export const serviceImagePath = (tenantId: string, title: string): string =>
  createBookingWebsiteDbRef(tenantId, `services/${title.trim().replace(/\s+/g, '_')}`);

/**
 * Add, update and delete services for one tenant, keeping the ['services', tenantId]
 * cache and the launch checklist in step. Shared by the Services page and the
 * Site Builder so both behave identically.
 */
export const useServiceActions = (tenantId: string) => {
  const queryClient = useQueryClient();
  const servicesKey = ['services', tenantId];
  const refreshDependents = () => {
    void queryClient.invalidateQueries({ queryKey: sitePublishingKeys.onboarding(tenantId), exact: true });
  };
  const replaceCache = (update: (services: Service[]) => Service[]) => {
    queryClient.setQueryData<Service[]>(servicesKey, (current) => update(current ?? []));
  };

  const add = useMutation({
    mutationFn: async ({ service, image }: { service: ServiceDraft; image?: File }) => {
      const id = await addService(service, image, tenantId);
      return {
        ...service,
        id,
        ...(image ? { imagePath: serviceImagePath(tenantId, service.title) } : {}),
      } as Service;
    },
    onSuccess: (created) => {
      replaceCache((services) => [...services, created]);
      refreshDependents();
    },
  });

  const update = useMutation({
    mutationFn: async ({ original, service, image }: { original: Service; service: Service; image?: File }) => {
      await updateService(original, service, image, tenantId);
      return service;
    },
    onSuccess: (saved) => {
      replaceCache((services) => services.map((service) => (service.id === saved.id ? saved : service)));
      refreshDependents();
    },
  });

  const remove = useMutation({
    mutationFn: async (service: Service) => {
      await deleteService(service, tenantId);
      return service;
    },
    onSuccess: (deleted) => {
      replaceCache((services) => services.filter((service) => service.id !== deleted.id));
      refreshDependents();
    },
  });

  return { add, update, remove };
};
